import { useCallback, useEffect, useRef, useState } from 'react';

import { formatarDataHora } from './formato';

/**
 * Rascunho automatico (p.16): "Formulario com mais de dez campos salva
 * rascunho automatico. Conexao em obra cai, e o preenchimento nao pode se
 * perder."
 *
 * O formulario entrega o que tem preenchido (`valor`) e como devolver isso aos
 * campos (`restaurar`). O rascunho vai para o `localStorage` meio segundo
 * depois da ultima mudanca, volta sozinho quando a tela abre de novo, e sai
 * quando o formulario salva (`descartar`) ou volta a ficar em branco.
 *
 * O armazenamento esta dentro de `try/catch` em toda chamada, como o tema
 * (`lib/tema`): navegador com armazenamento bloqueado LEVANTA em vez de
 * devolver nulo, e rascunho e conveniencia — nunca pode derrubar o formulario.
 *
 * A CHAVE LEVA A EMPRESA. O mesmo navegador pode entrar em duas empresas, e o
 * rascunho de uma nao pode aparecer preenchido na outra: quem chama escreve
 * algo como `equipamento-novo:${empresaId}`.
 */

const PREFIXO = 'ct-frotas:rascunho:';
const ESPERA_MS = 500;

export interface RascunhoGuardado<T> {
  readonly gravadoEm: string;
  readonly valor: T;
}

/** O pedaco do `Storage` que o rascunho usa. No teste, um `Map` resolve. */
export interface Armazem {
  getItem(chave: string): string | null;
  setItem(chave: string, valor: string): void;
  removeItem(chave: string): void;
}

export function lerRascunho<T>(armazem: Armazem | null, chave: string): RascunhoGuardado<T> | null {
  if (armazem === null) return null;
  try {
    const bruto = armazem.getItem(PREFIXO + chave);
    if (bruto === null) return null;
    const dado: unknown = JSON.parse(bruto);
    if (typeof dado !== 'object' || dado === null || !('valor' in dado) || !('gravadoEm' in dado)) return null;
    if (typeof dado.gravadoEm !== 'string') return null;
    return { gravadoEm: dado.gravadoEm, valor: dado.valor as T };
  } catch {
    return null;
  }
}

export function gravarRascunho<T>(armazem: Armazem | null, chave: string, valor: T, quando: Date): void {
  try {
    armazem?.setItem(PREFIXO + chave, JSON.stringify({ gravadoEm: quando.toISOString(), valor }));
  } catch {
    // Armazenamento cheio ou bloqueado: o formulario segue sem rascunho.
  }
}

export function apagarRascunho(armazem: Armazem | null, chave: string): void {
  try {
    armazem?.removeItem(PREFIXO + chave);
  } catch {
    // Armazenamento bloqueado: nao ha o que apagar.
  }
}

/** O aviso que o formulario mostra quando o rascunho volta (p.23: fato, depois o que fazer). */
export function textoDoRascunho(gravadoEm: Date): string {
  return `Rascunho de ${formatarDataHora(gravadoEm)} recuperado. Confira os campos antes de salvar.`;
}

function armazemDoNavegador(): Armazem | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function useRascunho<T>(
  chave: string,
  valor: T,
  restaurar: (valor: T) => void,
): { readonly recuperadoEm: Date | null; readonly descartar: () => void } {
  const [recuperadoEm, setRecuperadoEm] = useState<Date | null>(null);
  /* O formulario em branco: igual a ele, o rascunho sai em vez de ser gravado. */
  const emBranco = useRef(JSON.stringify(valor));
  const restaurou = useRef(false);
  const devolver = useRef(restaurar);
  useEffect(() => {
    devolver.current = restaurar;
  }, [restaurar]);

  useEffect(() => {
    const guardado = lerRascunho<T>(armazemDoNavegador(), chave);
    if (guardado !== null) {
      devolver.current(guardado.valor);
      setRecuperadoEm(new Date(guardado.gravadoEm));
    }
    restaurou.current = true;
  }, [chave]);

  useEffect(() => {
    if (!restaurou.current) return;
    const texto = JSON.stringify(valor);
    const relogio = setTimeout(() => {
      if (texto === emBranco.current) apagarRascunho(armazemDoNavegador(), chave);
      else gravarRascunho(armazemDoNavegador(), chave, valor, new Date());
    }, ESPERA_MS);
    return () => {
      clearTimeout(relogio);
    };
  }, [chave, valor]);

  const descartar = useCallback(() => {
    apagarRascunho(armazemDoNavegador(), chave);
    setRecuperadoEm(null);
  }, [chave]);

  return { recuperadoEm, descartar };
}
